import { useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import type { Key } from 'chessground/types';
import { Board, type BoardMoveEvent } from '@/chess/board';
import { KidHint, TurnIndicator, UnderstandingPrompt } from '@/ui';
import { useAwardXp } from '@/api/xp';
import { classifySuccessOutcome, type Outcome, type FailOutcome } from '@/api/outcome';
import type { DrillPosition, RepertoireDetail } from './types';
import { openingLabApi, type AttemptPayload } from './api';
import { pickUnseenPosition } from './drillPickers';

// Barème XP : +3 par succès en rappel d'ouverture (mémoire théorique, cf.
// docs/XP_BAREME.md). T5 MVP2.5 — persistance backend via useAwardXp.
const XP_PER_RECALL_SUCCESS = 3;

interface Props {
  repertoire: RepertoireDetail;
  positions: DrillPosition[];
  /**
   * Notifie un score (succès=1, fail=0) au parent. Le 2e argument
   * `meta.awardedXp` indique si l'award XP backend a été persisté
   * (false = déjà awardé précédemment, fix idempotence permanente
   * 2026-05-17). Si undefined (modes legacy non-persistés), le parent
   * incrémente l'XP local comme avant.
   */
  onResult: (
    result: 'success' | 'partial' | 'fail',
    meta?: { awardedXp?: boolean },
  ) => void;
}

/**
 * Mode `recall` : tire une position au hasard, demande à Léon de jouer le coup
 * attendu (drag/click/clavier). 2 essais max, puis affichage du coup correct
 * + commentaire éventuel. Toujours log côté backend pour spaced repetition.
 */
export function DrillRecall({ repertoire, positions, onResult }: Props) {
  // Fix 2026-05-17 : éviter les répétitions consécutives ("ce sont souvent
  // les mêmes exercices" signalé par Léon). Set in-memory des positions déjà
  // proposées dans la session ; quand le pool de non-vus est épuisé, on
  // reset le Set pour permettre un nouveau cycle (pas de blocage à 0
  // exercices). Persiste pendant le mount du composant — disparaît au
  // refresh/unmount (acceptable : 30+ positions par répertoire, peu probable
  // de toutes les drill en une session).
  const seenRef = useRef<Set<string>>(new Set());
  const [position, setPosition] = useState<DrillPosition>(() => pickUnseenPosition(positions, seenRef.current));
  const [attempts, setAttempts] = useState(0);
  const [phase, setPhase] = useState<'playing' | 'revealed'>('playing');
  const [lastFail, setLastFail] = useState<{
    playedMove: string;
    timeSpentMs: number;
    outcomeLogged: boolean;
  } | null>(null);
  // Léon a échoué 2/2 sur la position courante puis cliqué « Réessayer » pour
  // s'entraîner à appliquer le coup qu'il vient de voir. Pendant ce mode on
  // ne loggue plus aucune tentative (succès comme échec) — sinon le KPI
  // success_rate serait gonflé artificiellement et la carte SRS rejouée
  // alors qu'elle a déjà été créée par le fail initial.
  const [isRetrying, setIsRetrying] = useState(false);
  const startedAtRef = useRef<number>(performance.now());
  const awardXp = useAwardXp();

  useEffect(() => {
    startedAtRef.current = performance.now();
    setAttempts(0);
    setPhase('playing');
    setLastFail(null);
    setIsRetrying(false);
  }, [position]);

  const chess = useMemo(() => new Chess(position.fenBefore), [position]);

  const legalMoves = useMemo(() => {
    const map = new Map<Key, Key[]>();
    for (const move of chess.moves({ verbose: true })) {
      const list = map.get(move.from as Key) ?? [];
      list.push(move.to as Key);
      map.set(move.from as Key, list);
    }
    return map;
  }, [chess]);

  function pickNext() {
    // T9.1 garde-fou : si Léon clique « Position suivante » sans répondre au
    // prompt d'échec, on log quand même l'attempt (outcome=null = legacy
    // MVP1.x). Sinon le KPI `recurring_mistake_rate` (T9.2) sous-estime les
    // vraies erreurs et la carte SRS n'est jamais créée.
    if (lastFail && !lastFail.outcomeLogged) {
      logAttempt('fail', lastFail.playedMove, lastFail.timeSpentMs, undefined);
    }
    setPosition(pickUnseenPosition(positions, seenRef.current));
  }

  function logAttempt(
    result: AttemptPayload['result'],
    played: string | undefined,
    timeSpentMs: number,
    outcome?: Outcome,
  ) {
    const payload: AttemptPayload = {
      module: 'opening',
      slug: `${repertoire.slug}:ply${position.ply}`,
      fen: position.fenBefore,
      side_to_move: position.sideToMove,
      mode: 'recall',
      result,
      time_spent_ms: timeSpentMs,
      source_ref: repertoire.slug,
      expected_move: position.expectedUci,
      ...(played ? { played_move: played } : {}),
      ...(outcome ? { outcome } : {}),
    };
    openingLabApi.postAttempt(payload).catch((e) => console.error('[recall] log failed', e));
    if (result === 'success') {
      // Fix idempotence permanente 2026-05-17 : on attend la réponse
      // backend pour savoir si l'XP est vraiment awardé (false si la
      // position a déjà été drillée), et on propage le flag au parent
      // qui décide d'incrémenter l'XP local ou pas. Sinon Léon rejoue
      // une position déjà recall-ée → backend skip mais frontend
      // afficherait +3 XP local → divergence au refresh.
      awardXp.mutate(
        {
          module: 'opening',
          source_ref: `${repertoire.slug}:ply${position.ply}`,
          xp_amount: XP_PER_RECALL_SUCCESS,
        },
        {
          onSuccess: (data) => {
            onResult(result, { awardedXp: data.awarded });
          },
          onError: () => {
            // Backend KO : fallback comportement legacy (award local).
            // Cas typique : pas authentifié (401), endpoint absent (404),
            // serveur down. Mieux vaut un award local visuel que rien.
            onResult(result, { awardedXp: true });
          },
        },
      );
      return;
    }
    onResult(result);
  }

  function handleMove({ from, to }: BoardMoveEvent) {
    if (phase === 'revealed') return;
    // F-5 P1 : chess.js v1 throw sur coup illégal (au lieu de return null
    // comme v0). Board.tsx filtre déjà via Chessground `dests`, mais une
    // race condition tactile peut faire passer un coup illégal. Sans le
    // try/catch ce composant crashait, ErrorBoundary remontait, Léon
    // perdait sa position.
    let move;
    try {
      move = chess.move({ from, to, promotion: 'q' });
    } catch {
      return;
    }
    if (!move) return;
    const playedUci = `${move.from}${move.to}${move.promotion ?? ''}`;
    if (playedUci === position.expectedUci) {
      // T9.1 — succès : outcome inféré automatiquement.
      // Si Léon a essayé 1 fois déjà → success_slow (compréhension après essai),
      // sinon classification par temps (success_clean si rapide).
      // En mode retry, on ne loggue pas (Léon rejoue après avoir vu la
      // solution — pas un vrai succès, juste de l'ancrage).
      if (!isRetrying) {
        const timeSpentMs = Math.round(performance.now() - startedAtRef.current);
        const outcome: Outcome = attempts > 0 ? 'success_slow' : classifySuccessOutcome({ timeSpentMs });
        logAttempt('success', playedUci, timeSpentMs, outcome);
      }
      setPhase('revealed');
      return;
    }
    chess.undo();
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    if (newAttempts >= 2) {
      if (!isRetrying) {
        // T9.1 — fail : on attend la réponse "tu as compris ?" avant de logger.
        const timeSpentMs = Math.round(performance.now() - startedAtRef.current);
        setLastFail({ playedMove: playedUci, timeSpentMs, outcomeLogged: false });
      }
      setPhase('revealed');
    }
  }

  function handleUnderstanding(understood: boolean): void {
    if (!lastFail || lastFail.outcomeLogged) return;
    const outcome: FailOutcome = understood ? 'fail_understood' : 'fail_not_understood';
    logAttempt('fail', lastFail.playedMove, lastFail.timeSpentMs, outcome);
    setLastFail({ ...lastFail, outcomeLogged: true });
  }

  /**
   * Léon clique « Réessayer » après avoir vu la solution. On veut qu'il
   * puisse rejouer la même position autant de fois qu'il veut sans changer
   * de drill et sans biaiser les KPIs. Si l'outcome du fail initial n'a pas
   * encore été enregistré (Léon n'a pas cliqué « Tu as compris ? »), on le
   * loggue automatiquement comme `fail_not_understood` — cliquer Réessayer
   * = signal implicite qu'il a besoin de plus d'entraînement. Pas de re-log
   * pendant le retry (cf. `isRetrying`).
   */
  function retryPosition(): void {
    if (lastFail && !lastFail.outcomeLogged) {
      logAttempt('fail', lastFail.playedMove, lastFail.timeSpentMs, 'fail_not_understood');
      setLastFail({ ...lastFail, outcomeLogged: true });
    }
    startedAtRef.current = performance.now();
    setAttempts(0);
    setPhase('playing');
    setIsRetrying(true);
  }

  return (
    <div className="flex flex-col gap-4">
      <KidHint tone="violet" title="Pourquoi ce drill ?">
        Tu mémorises les premiers coups de tes ouvertures pour ne plus jamais
        hésiter en début de partie. Plus tu connais ta théorie, plus tu peux
        penser au plan.
      </KidHint>
      <header className="flex items-baseline justify-between">
        <p className="text-caption uppercase text-violet-500">Mémoire · coup {position.ply} · {position.sideToMove === 'white' ? 'Blancs' : 'Noirs'}</p>
        <p className="text-body-sm text-ink-300">Essai {attempts + 1} / 2</p>
      </header>

      <TurnIndicator state={phase === 'playing' ? 'user-turn' : 'idle'} idleLabel="Coup révélé" />

      <Board
        fen={position.fenBefore}
        orientation={repertoire.color}
        interactive={phase === 'playing'}
        legalMoves={legalMoves}
        turnColor={position.sideToMove}
        onMove={handleMove}
      />

      {phase === 'revealed' && (
        <section className="rounded-xl border border-ink-700 bg-ink-800 p-4">
          <p className="text-body">
            Coup attendu :{' '}
            <strong className="font-mono text-acid-500">{position.expectedSan}</strong>
          </p>
          {position.comment && (
            <p className="mt-2 text-body-sm text-ink-300">{stripTags(position.comment)}</p>
          )}
          {lastFail && !isRetrying && (
            <UnderstandingPrompt
              done={lastFail.outcomeLogged}
              onAnswer={handleUnderstanding}
            />
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {lastFail && (
              <button
                type="button"
                onClick={retryPosition}
                className="rounded-lg border border-violet-500/60 bg-violet-500/10 px-4 py-2 font-display text-body text-violet-500 transition hover:bg-violet-500/20"
              >
                Réessayer
              </button>
            )}
            <button
              type="button"
              onClick={pickNext}
              className="rounded-lg bg-violet-500 px-4 py-2 font-display text-body text-paper-50 transition hover:opacity-90"
            >
              Position suivante
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function stripTags(comment: string): string {
  return comment
    .replace(/#critical/g, '')
    .replace(/#trap/g, '')
    .replace(/#plan\s+/g, '')
    .trim();
}
